import { z } from 'zod';

/**
 * How a push is sent (BB-72, PUSH-006): lifetime at the provider, priority, replacement and expiry. Defaults come
 * from the environment push settings (BB-77) and the message (BB-76); the wire builders map them to FCM
 * `android.ttl` / `priority` / `collapse_key` and APNs `apns-expiration` / `apns-priority` / `apns-collapse-id`.
 */
export const PUSH_PLATFORMS = ['android', 'ios'] as const;
export type PushPlatform = (typeof PUSH_PLATFORMS)[number];

export const APNS_ENVIRONMENTS = ['sandbox', 'production'] as const;
export type ApnsEnvironment = (typeof APNS_ENVIRONMENTS)[number];

/** FCM's maximum message lifetime (28 days), also the default in Braze push settings. */
export const PUSH_MAX_TTL_SECONDS = 2_419_200;
/** `apns-collapse-id` limit; FCM `collapse_key` gets the same one. */
export const PUSH_COLLAPSE_KEY_MAX_BYTES = 64;

export const DeliveryOptions = z.strictObject({
  /** Lifetime at the provider; 0 — deliver now or drop. Capped by `expiresAt` when that is sooner. */
  ttlSeconds: z.int().min(0).max(PUSH_MAX_TTL_SECONDS),
  /** `high` wakes the device (FCM HIGH, APNs 10); silent pushes are always sent as normal. */
  priority: z.enum(['normal', 'high']),
  /** A newer push with the same key replaces the older one at the provider and on the device. */
  collapseKey: z
    .string()
    .min(1)
    .refine((key) => Buffer.byteLength(key, 'utf8') <= PUSH_COLLAPSE_KEY_MAX_BYTES, `at most ${PUSH_COLLAPSE_KEY_MAX_BYTES} bytes`)
    .optional(),
  androidNotificationId: z.int32().optional(),
  /** Never sent or shown after this moment (the worker expires the delivery, the SDK drops a late push). */
  expiresAt: z.iso.datetime({ offset: true }).optional(),
});
export type DeliveryOptions = z.infer<typeof DeliveryOptions>;

/** Which installations of a profile get the push (BB-76). */
export const PushTargetSelector = z.strictObject({
  appInstanceIds: z.array(z.uuid()).min(1).max(50).optional(),
  platforms: z.array(z.enum(PUSH_PLATFORMS)).min(1).max(PUSH_PLATFORMS.length),
  /** `most_recent_device` — only the installation seen last. */
  destination: z.enum(['all_devices', 'most_recent_device']).default('all_devices'),
  iosDeviceFamily: z.enum(['all', 'iphone', 'ipad']).default('all'),
});
export type PushTargetSelector = z.infer<typeof PushTargetSelector>;
